define((require) => {
    "use strict";

    const Backbone          = require("backbone");
    const EditorEvents      = require("shared/services/EditorEvents");
    const ImprovedView      = require("editor/zbone/ImprovedView");
    const Permissions       = require("shared/security/Permissions");
    const PublishableState  = require("util/PublishableState");
    const Syncer            = require("shared/services/sync/Syncer");
    const SystemMessages    = require("editor/services/SystemMessages");
    const Tooltip           = require("ui/common/Tooltip");
    const template          = require("ui/editor/editorMoreMenu/PushPublishButtonView.tpl");
    const LoggedInUserState = require("shared/ui/user/LoggedInUserState");
    const ResourceCombined  = require("shared/util/ResourceCombined");
    const {COPY_CODE_BUTTON_STATE, BUTTON_STATUS} = require("shared/util/copyCode");
    const Configuration = require("shared/services/Configuration");
    const Storage       = require("shared/services/Storage");

    const PUSH_TOOLTIP_TEXT = {
        SPECTATOR_MODE:   "You do not have permission to push.",
        DEFAULT:          "Pushing your Experience will make it available for Drag'n Drop Publishing. Changes made to your Experience will only go live after you Push again.",
        EMPTY:            ""
    };
    const PUBLISH_TOOLTIP_HTML = {
        EXPERIENCE_NOT_PUSHED: "<i class=\"glyphicon glyphicon-exclamation-sign\"></i> This Experience is not Pushed",
        VARIANT_NOT_PUSHED:    "<i class=\"glyphicon glyphicon-exclamation-sign important\"></i> The current version is not Pushed",
        EMPTY:                 ""
    };

    /**
     * Component representing the Push and Publish buttons for the <tt>EditorPreviewMenu</tt>.
     *
     * @cfg {ExperienceModel} model The current experience model.
     *
     * @see EditorPreviewMenu
     *
     * @class PushPublishButtonView
     */
    return ImprovedView.extend({

        className: "push-button-container",

        template,

        ui: {
            pushBtn:    ".push-btn",
            publishBtn: ".publish-btn"
        },

        events: {
            "click @ui.pushBtn":    "_push",
            "click @ui.publishBtn": "_gotoPublish"
        },

        initialize() {
            this.listenTo(this.model, "sync:state", (state, method) => {
                if (method !== "destroy") {
                    if (state === Syncer.State.Synced || state === Syncer.State.Failed) {
                        this._updatePushPublishState();
                    }
                }
            });
        },

        onRender() {
            this._updatePushPublishState();

            if (this.options.hidePublish) {
                this.ui.publishBtn.hide();
            }
            if (this.options.hidePushBtn) {
                this.ui.pushBtn.hide();
            }
        },

        onClose() {
            // Chance for "hanging" tooltips - make sure they are removed...
            const {pushBtn, publishBtn} = this.ui;

            Tooltip.remove(pushBtn);
            Tooltip.remove(publishBtn);
        },

        _push() {
            const isCombinedGroupEnabled = LoggedInUserState.get("companySettings").get("combinedGroupsSpeed");
            const isGroupType = !!this.model.get("group");
            const isGroupPushed = isGroupType  && this.model.get("state") === "Pushed";
            const cannotPush = isCombinedGroupEnabled && isGroupType && !isGroupPushed;

            // Has privilege to push?
            if (Permissions.Privileges.PUBLISH.cannot() || cannotPush) {
                return;
            }
            // Notify the user that a save is in progress and the Push will occur next
            if (Syncer.State.current(this.model) === Syncer.State.Pending) {
                SystemMessages.notify("Push will occur once save completes.", 2000);
            }
            this.model.set(COPY_CODE_BUTTON_STATE, BUTTON_STATUS.disabled);
            // Push the experience
            this.model.push()
                .then(() => {
                    const token = Storage.Local.get(Storage.Keys.LOGIN, "token") || Storage.Session.get(Storage.Keys.LOGIN, "token");
                    fetch(
                        `${Configuration.creatorSettingApiBaseUrl}/v1/buildSsr?companyId=${this.model.get("companyId")}&experienceId=${this.model.get("id")}`,
                        {
                            headers: {
                                Authorization: `Bearer ${token}`
                            },
                            method: "GET"
                        }
                    )
                        .catch();
                    const purgeTime = this.model.get("purgeTime");

                    // Only open the notification bar if the purgeTime is not null.
                    if (purgeTime !== null) {
                        SystemMessages.notifyPurge("Experience", "pushed", purgeTime, false);
                    }

                    var eventProperties = {
                        experienceId: this.model.get("id"),
                        experienceVersion: this.model.get("version"),
                        experiencePushedVersion: this.model.get("pushedVersion"),
                        projectId: this.options.project && this.options.project.get("id"),
                        triggerOrigin: "PushExperienceButton"
                    };

                    EditorEvents.trigger(EditorEvents.constants.PUSH_EXPERIENCE, eventProperties);
                    this.model.getNativeCode(eventProperties.triggerOrigin);
                }).then(() => {
                    if (!isCombinedGroupEnabled) {
                        return;
                    }
                    return ResourceCombined.syncExperience(this.model.get("id"), this.model.get("companyId"));
                });
        },

        _gotoPublish() {
            const {project} = this.options;

            if (typeof project === "undefined") {
                Backbone.history.navigate("publish", true);
            } else {
                Backbone.history.navigate(`#project/${project.get("id")}/publish`, true);
            }

            const eventProperties = {
                experienceId: this.model.get("id"),
                experienceVersion: this.model.get("version"),
                experiencePushedVersion: this.model.get("pushedVersion"),
                dashboardType: "Publish",
                projectId: this.options.project && this.options.project.get("id"),
                triggerOrigin: "PublishButton"
            };

            EditorEvents.trigger(EditorEvents.constants.OPEN_DASHBOARD, eventProperties);
            // Hide the tooltip once the button is clicked, otherwise the tooltip may stick around while page transition out.
            Tooltip.hide(this.ui.publishBtn);
        },

        /**
         * @private
         * Handler called to update push and publish buttons.
         */
        _updatePushPublishState() {
            // No permission, so the push button can never be active...
            if (Permissions.Privileges.PUBLISH.cannot()) {
                return this.toggleButtons(true, PUSH_TOOLTIP_TEXT.EMPTY, PUBLISH_TOOLTIP_HTML.EMPTY);
            }

            // Default tooltip...
            const pushTooltip = PUSH_TOOLTIP_TEXT.DEFAULT;
            let publishTooltip = PUBLISH_TOOLTIP_HTML.EMPTY;

            if (!this.model.hasAtLeastState(PublishableState.PUSHED)) {
                publishTooltip = PUBLISH_TOOLTIP_HTML.EXPERIENCE_NOT_PUSHED;
            } else if (this.model.isChangedAfterPush()) {
                publishTooltip = PUBLISH_TOOLTIP_HTML.VARIANT_NOT_PUSHED;
            }

            // Push button enabled/disabled? We will make push button disabled if it is all pushed
            const disabled = !this.model.hasNonPushedChanges() && this.model.syncState.actual.state !== Syncer.State.Failed;

            // Push button should be disabled if current version of the experience is pushed
            this.toggleButtons(disabled, pushTooltip, publishTooltip);
        },

        /**
         * Toggles the state of the push button and sets the tooltips for the push and publish buttons.
         *
         * @param {Boolean} disabled         True if the push button is disabled, false otherwise.
         * @param {String}  [pushTooltip]    The tooltip for the push button; can be falsey (removed).
         * @param {String}  [publishTooltip] The tooltip for the publish button; can be falsey (removed).
         */
        toggleButtons(disabled, pushTooltip, publishTooltip) { // also called by controller!
            this.ui.pushBtn.toggleClass("disabled", disabled);
            this.ui.pushBtn.attr("disabled", disabled);

            this._updateButtonTooltip(this.ui.pushBtn,    pushTooltip);
            this._updateButtonTooltip(this.ui.publishBtn, publishTooltip, 230);
        },

        /**
         * @private
         * Updates the tooltip for the supplied button with the specified content and width, or removes it if
         * no content.
         *
         * @param {jQuery} button  The push or publish button; never falsey.
         * @param {String} content The content for the tooltip; can be falsey, which will remove the tooltip.
         * @param {Number} [width] The tooltip width; defaults to standard tooltip width.
         */
        _updateButtonTooltip(button, content, width) {
            if (content) {
                var tooltipConfig = {
                    content: content,
                    width: width || 300,
                    html: true
                };

                if (button === this.ui.pushBtn) {
                    tooltipConfig.offset = {
                        align: "left",
                        elementWidth: 100
                    };
                }

                Tooltip.create(button, tooltipConfig);
            } else {
                Tooltip.remove(button);
            }
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/editorMoreMenu/PushPublishButtonView.js